/**
 * Receipt detail sticky rail — `prototype/sales/invoice-detail.html` ▸ `.rail`
 * adapted. Markup mirrors invoice-rail so it picks up the shared chrome
 * from receipt-detail.css.
 *
 * Four cards: next-action (sky-tinted), money, customer, audit. Markup
 * uses the prototype's class conventions verbatim:
 *   `.rail-card.rail-next` → `.head` + `.body` with `.pri-line` + `.actions .btn`
 *   `.rail-card.rail-money` → `.head` + `.body` with `.row` + `.row.grand` + `.v`
 *   `.rail-card.rail-party` → `.head` + `.body` with `.who` + `.ava` + `.copy`
 *   `.rail-card` (default) → `.head` + `.body` with `.row` + `.k` + `.v`
 */
import { Link } from "@tanstack/react-router";
import { Mail, Phone } from "lucide-react";
import { initials } from "../../../lib/initials.ts";
import { formatDate, paiseToInrShort } from "../../../lib/format.ts";

export interface ReceiptRailProps {
  receipt: {
    id: string;
    receivedOn: string;
    amountPaise: string;
    tdsPaise: string;
    fxGainLossPaise: string;
    settlementRatePaise: string | null;
    mode: string;
    reference: string | null;
    voidsId: string | null;
    allocations: Array<{ invoiceId: string; paise: string; allocatedOn: string }>;
    createdAt: string;
  };
  customer: {
    id: string;
    name: string;
    tdsSection: string | null;
    isTdsApplicable: boolean;
  };
  invoice: { id: string; docNumber: string | null; currencyCode: string } | null;
}

export function ReceiptRail({ receipt, customer, invoice }: ReceiptRailProps) {
  return (
    <aside className="rail" aria-label="Receipt context rail">
      {/* Money card removed — the same numbers (Bank credited / Applied
       *  to invoice / FX g/l) already render in the voucher's
       *  .doc-totals stack on the left. Duplicating them in the rail
       *  is noise. Allocation is the first rail card now. */}

      {/* Allocation — uses `.rail-card` body with simple `.row` markup */}
      <section className="rail-card">
        <div className="head">
          <h4>Allocated to</h4>
        </div>
        <div className="body">
          {receipt.allocations.map((alloc) => {
            const docNumber =
              invoice && alloc.invoiceId === invoice.id
                ? (invoice.docNumber ?? "Draft")
                : `INV-${alloc.invoiceId.slice(0, 8)}`;
            return (
              <div
                key={alloc.invoiceId}
                className="row"
                style={{ flexDirection: "column", alignItems: "stretch", gap: 2 }}
              >
                <div
                  style={{
                    display: "flex",
                    alignItems: "baseline",
                    justifyContent: "space-between",
                    gap: 8,
                  }}
                >
                  <Link
                    to="/sales/invoices/$invoiceId"
                    params={{ invoiceId: alloc.invoiceId }}
                    className="num"
                    style={{ color: "var(--sky-700)", fontWeight: 600 }}
                  >
                    {docNumber}
                  </Link>
                  <span className="v">{paiseToInrShort(BigInt(alloc.paise))}</span>
                </div>
                <span style={{ color: "var(--muted)", fontSize: 12 }}>
                  Allocated {formatDate(alloc.allocatedOn)}
                </span>
              </div>
            );
          })}
        </div>
      </section>

      {/* Customer — rail-party with `.who` + `.ava` + `.copy` */}
      <section className="rail-card rail-party">
        <div className="head">
          <h4>Customer</h4>
          <Link
            to="/sales/customers/$customerId"
            params={{ customerId: customer.id }}
            className="num"
            style={{ color: "var(--sky-700)", fontSize: 12, fontWeight: 600 }}
          >
            Open →
          </Link>
        </div>
        <div className="body">
          <div className="who">
            <div className="ava" aria-hidden>
              {initials(customer.name)}
            </div>
            <div className="copy">
              <div className="name">{customer.name}</div>
              <div className="field">
                <span className="k">TDS</span>
                <span className="v">
                  {customer.isTdsApplicable && customer.tdsSection
                    ? customer.tdsSection
                    : "None on file"}
                </span>
              </div>
            </div>
          </div>
          <div className="actions" style={{ marginTop: 10 }}>
            <button
              type="button"
              className="btn secondary"
              disabled
              title="Call lands when phone is on file"
            >
              <Phone size={11} strokeWidth={2.2} /> Call
            </button>
            <button type="button" className="btn secondary" disabled title="Email lands in V1.x">
              <Mail size={11} strokeWidth={2.2} /> Email
            </button>
          </div>
        </div>
      </section>

      {/* Audit — rail-card with `.row` + `.k`/`.v` */}
      <section className="rail-card">
        <div className="head">
          <h4>Audit</h4>
        </div>
        <div className="body">
          <div className="row">
            <span>Mode</span>
            <span className="v">{receipt.mode.toUpperCase()}</span>
          </div>
          <div className="row">
            <span>Reference</span>
            <span
              className="v"
              style={{
                maxWidth: 160,
                overflow: "hidden",
                textOverflow: "ellipsis",
                whiteSpace: "nowrap",
              }}
            >
              {receipt.reference || "—"}
            </span>
          </div>
          {receipt.settlementRatePaise ? (
            <div className="row">
              <span>Settlement rate</span>
              <span className="v">
                ₹{(Number(BigInt(receipt.settlementRatePaise)) / 100).toFixed(2)}
              </span>
            </div>
          ) : null}
          <div className="row">
            <span>Created</span>
            <span className="v">{formatDate(receipt.createdAt.slice(0, 10))}</span>
          </div>
          {receipt.voidsId ? (
            <div className="row out">
              <span>Voids</span>
              <span className="v">…{receipt.voidsId.slice(-8)}</span>
            </div>
          ) : null}
        </div>
      </section>
    </aside>
  );
}
